import { Link } from '@remix-run/react';
import type { RemixLinkProps } from '@remix-run/react/dist/components';

export type Variant =
  | 'disabled'
  | 'green-human'
  | 'green-cta'
  | 'black-cta'
  | 'forest'
  | 'purple-hola'
  | 'facebook-blue'
  | 'red-alert'
  | 'red-alert-transparent'
  | 'purple-hola-transparent'
  | 'white-borderless'
  | 'white'
  | 'black'
  | 'transparent-white-border'
  | 'transparent-black-border'
  | 'transparent-grey-border'
  | 'transparent-grey-bold'
  | 'white-grey-border'
  | 'none';

export type ButtonProps = React.HTMLProps<HTMLButtonElement> & {
  className?: string;
  variant: Variant;
};

const defaultClassNames =
  'rounded-lg px-4 py-2 text-center w-full font-semibold active:scale-95 transition duration-200 ease-in-out ';

const variantClassName = {
  black: 'bg-black text-white border border-black',
  'black-cta': 'bg-black text-white border border-white',
  disabled: 'bg-grey-hide text-white cursor-not-allowed active:scale-100',
  'facebook-blue':
    'bg-facebook-blue text-white hover:bg-blue-800 active:bg-blue-900',
  forest:
    'border border-forest bg-forest text-white hover:bg-green-700 hover:border-green-700 active:bg-green-700 active:border-green-700',
  'green-cta': 'bg-green-human text-black',
  'green-human':
    'border border-green-human bg-green-human text-white hover:bg-green-600 active:bg-green-700 hover:border-green-600 active:border-green-700',
  none: '',
  'purple-hola':
    'bg-purple-hola text-white hover:bg-purple-600 active:bg-purple-700',
  'purple-hola-transparent': 'border border-purple-hola text-purple-hola',
  'red-alert': 'bg-red-alert text-white',
  'red-alert-transparent': 'border border-red-alert text-red-alert',
  'transparent-black-border':
    'border border-black tracking-[0.24px] hover:opacity-70 active:opacity-50',
  'transparent-grey-bold':
    'border border-grey-hide bg-white text-bold text-grey-hide',
  'transparent-grey-border':
    'border border-grey text-grey-read tracking-[0.24px] text-xs hover:opacity-70 active:opacity-50',
  'transparent-white-border':
    'border border-white text-white hover:opacity-70 active:opacity-50',
  white: 'bg-white text-black border border-black',
  'white-borderless': 'bg-grey-tile text-black',
  'white-grey-border':
    'border border-grey bg-white text-grey-read tracking-[0.24px] text-xs hover:opacity-70 active:opacity-50'
};

const getClassNames = (variant: Variant) => {
  if (variant === 'none') return '';

  return defaultClassNames + ' ' + variantClassName[variant];
};

const Button = ({
  disabled,
  className,
  variant,
  children,
  onClick,
  ...props
}: ButtonProps) => {
  return (
    <button
      {...props}
      className={getClassNames(variant) + ' ' + className}
      disabled={disabled}
      type="submit"
      onClick={onClick}
    >
      {children}
    </button>
  );
};

export type ExternalLinkProps = React.HTMLProps<HTMLAnchorElement> & {
  variant: Variant;
};

const ExternalLink = ({
  rel,
  target,
  href,
  variant,
  children,
  className,
  ...props
}: ExternalLinkProps) => {
  return (
    <a
      href={variant === 'disabled' ? undefined : href}
      target={target}
      rel={rel}
      className={`${getClassNames(variant)} block ${className}`}
      {...props}
    >
      {children}
    </a>
  );
};

export type InternalLinkProps = RemixLinkProps & {
  variant: Variant;
};

const InternalLink = ({
  children,
  className,
  variant,
  ...props
}: InternalLinkProps) => {
  return (
    <Link {...props} className={`${getClassNames(variant)} block ${className}`}>
      {children}
    </Link>
  );
};

export { Button, ExternalLink, InternalLink };
